import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAccountData } from './AccountContext';
import {
  bookmarkEntryKey,
  loadStudioBookmarks,
  saveStudioBookmarks,
} from '../utils/studioBookmarks';

const StudioBookmarksContext = createContext(null);

export function StudioBookmarksProvider({ children }) {
  const { auth } = useAccountData();
  const userId = auth?.id || auth?._id || auth?.email || null;
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    setBookmarks(loadStudioBookmarks(userId));
  }, [userId]);

  const isBookmarked = useCallback(
    (bookmark) => {
      if (!bookmark?.kind || bookmark.id == null) return false;
      const key = bookmarkEntryKey(bookmark);
      return bookmarks.some((b) => bookmarkEntryKey(b) === key);
    },
    [bookmarks],
  );

  const toggleBookmark = useCallback(
    (bookmark) => {
      if (!bookmark?.kind || bookmark.id == null) return;
      setBookmarks((prev) => {
        const key = bookmarkEntryKey(bookmark);
        const exists = prev.some((b) => bookmarkEntryKey(b) === key);
        const next = exists
          ? prev.filter((b) => bookmarkEntryKey(b) !== key)
          : [{ ...bookmark, bookmarkedAt: new Date().toISOString() }, ...prev];
        saveStudioBookmarks(userId, next);
        return next;
      });
    },
    [userId],
  );

  const removeBookmark = useCallback(
    (bookmark) => {
      if (!bookmark?.kind || bookmark.id == null) return;
      const key = bookmarkEntryKey(bookmark);
      setBookmarks((prev) => {
        const next = prev.filter((b) => bookmarkEntryKey(b) !== key);
        saveStudioBookmarks(userId, next);
        return next;
      });
    },
    [userId],
  );

  const value = useMemo(
    () => ({
      bookmarks,
      isBookmarked,
      toggleBookmark,
      removeBookmark,
    }),
    [bookmarks, isBookmarked, toggleBookmark, removeBookmark],
  );

  return (
    <StudioBookmarksContext.Provider value={value}>{children}</StudioBookmarksContext.Provider>
  );
}

export function useStudioBookmarks() {
  const ctx = useContext(StudioBookmarksContext);
  if (!ctx) {
    throw new Error('useStudioBookmarks must be used within StudioBookmarksProvider');
  }
  return ctx;
}

export function useStudioBookmarksOptional() {
  return useContext(StudioBookmarksContext);
}
